import {convert as convertUnit} from 'convert';
import {formatDecimal} from '@/ts/utils/number-format';

const WEIGHT_DECIMALS = 1; // Decimal places for weight display (both imperial and metric)
const WEIGHT_STEP = 0.1; // Step value for weight input (both imperial and metric)

interface UnitConversionOptions {
  countryCode?: string;
  /** The reader's `Intl` locale (`useDateFormat().numberLocale`) for the numbers it writes out. */
  locale?: string;
}

interface UnitConversionReturn {
  isImperial: boolean;
  gramsToUserUnit(grams: number): number;
  userUnitToGrams(value: number): number;
  getWeightUnit(): string;
  centimetersToUserUnit(cm: number): {feet?: number; inches?: number; cm?: number};
  userUnitToCentimeters(value: {feet?: number; inches?: number; cm?: number}): number;
  getHeightUnits(): string[];
  formatWeight(grams: number): string;
  formatHeight(cm: number): string;
}

function useUnitConversion(options: UnitConversionOptions = {}): UnitConversionReturn {
  const {countryCode = '', locale = 'en-US'} = options;

  // Determine if imperial system should be used (US, Myanmar, Liberia)
  const imperialCountries = ['US', 'MM', 'LR'];
  const isImperial = imperialCountries.includes(countryCode);

  // Weight conversions
  const gramsToUserUnit = (grams: number): number => {
    if (isImperial) {
      return convertUnit(grams, 'grams').to('pounds');
    }
    return convertUnit(grams, 'grams').to('kilograms');
  };

  const userUnitToGrams = (value: number): number => {
    if (isImperial) {
      return convertUnit(value, 'pounds').to('grams');
    }
    return convertUnit(value, 'kilograms').to('grams');
  };

  const getWeightUnit = (): string => {
    return isImperial ? 'lbs' : 'kg';
  };

  // Height conversions
  const centimetersToUserUnit = (cm: number): {feet?: number; inches?: number; cm?: number} => {
    if (isImperial) {
      const totalInches = convertUnit(cm, 'centimeters').to('inches');
      let feet = Math.floor(totalInches / 12);
      let inches = Math.round(totalInches % 12);

      // Handle edge case where rounding inches results in 12
      if (inches === 12) {
        feet += 1;
        inches = 0;
      }

      return {feet, inches};
    }
    return {cm};
  };

  const userUnitToCentimeters = (value: {feet?: number; inches?: number; cm?: number}): number => {
    if (isImperial && typeof value.feet !== 'undefined') {
      const totalInches = (value.feet * 12) + (value.inches ?? 0);
      return convertUnit(totalInches, 'inches').to('centimeters');
    }
    return value.cm ?? 0;
  };

  const getHeightUnits = (): string[] => {
    return isImperial ? ['ft', 'in'] : ['cm'];
  };

  // Format weight for display
  const formatWeight = (grams: number): string => {
    const value = gramsToUserUnit(grams);
    const unit = getWeightUnit();
    return `${formatDecimal(value, locale, WEIGHT_DECIMALS)} ${unit}`;
  };

  // Format height for display
  const formatHeight = (cm: number): string => {
    const values = centimetersToUserUnit(cm);
    if (isImperial && typeof values.feet !== 'undefined' && typeof values.inches !== 'undefined') {
      return `${values.feet}' ${values.inches}"`;
    }
    return `${cm} cm`;
  };

  return {
    isImperial,
    gramsToUserUnit,
    userUnitToGrams,
    getWeightUnit,
    centimetersToUserUnit,
    userUnitToCentimeters,
    getHeightUnits,
    formatWeight,
    formatHeight,
  };
}

export {useUnitConversion};
export type {UnitConversionOptions};
export {WEIGHT_DECIMALS, WEIGHT_STEP};